// MeetingTopics — the meeting agenda: a checkbox list of discussion topics, shared by the
// in-call panel (MeetingRoom, live via the MeetingTopics broadcast) and the meeting-card
// modal (fetch + refetch). Staff add/edit/delete/tick; everyone else sees a read-only live
// list. Topics tick themselves as they're discussed (the server-side matcher) — a manual
// untick locks a topic against being auto-ticked again.

import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { MeetingsApi } from "../hooks/useApi.js";

export function TopicList({ code, topics, canEdit, onMutated }) {
  const [text, setText] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [confirmDel, setConfirmDel] = useState(null); // topic id awaiting delete confirmation
  const [confirmClear, setConfirmClear] = useState(false); // "Clear all" armed (inline confirm, like delete)
  const [busy, setBusy] = useState(false);
  const [genBusy, setGenBusy] = useState(false);
  const [error, setError] = useState("");

  const run = async (fn) => {
    setBusy(true); setError("");
    try { await fn(); onMutated?.(); }
    catch (e) { setError(e?.body?.error || "That didn't save — try again."); }
    finally { setBusy(false); }
  };

  const add = () => {
    const t = text.trim();
    if (!t) return;
    run(async () => { await MeetingsApi.addTopic(code, t); setText(""); });
  };
  const saveEdit = () => {
    if (editingId == null) return;
    const id = editingId, t = editText.trim();
    setEditingId(null);
    if (t) run(() => MeetingsApi.editTopic(code, id, t));
  };
  const generate = async () => {
    setGenBusy(true); setError("");
    try { await MeetingsApi.generateTopics(code); onMutated?.(); }
    catch (e) { setError(e?.body?.error || "Couldn't generate topics."); }
    finally { setGenBusy(false); }
  };

  return (
    <div className="topics">
      {error && <div className="topics__err">⚠ {error}</div>}
      {topics.length === 0 && (
        <div className="topics__empty">
          {canEdit ? "No topics yet — add one, or let AI suggest an agenda." : "No topics on the agenda yet."}
        </div>
      )}
      <ul className="topics__list">
        {topics.map((t) => (
          <li key={t.id} className={`topics__item ${t.done ? "topics__item--done" : ""}`}>
            <button type="button" className="topics__check" disabled={!canEdit || busy}
                    title={!canEdit ? "" : t.done ? "Untick (it won't auto-tick again)" : "Mark as discussed"}
                    onClick={() => canEdit && run(() => MeetingsApi.tickTopic(code, t.id, !t.done))}>
              {t.done ? "✓" : ""}
            </button>
            {editingId === t.id ? (
              <input className="topics__editinput" autoFocus value={editText} maxLength={300}
                     onChange={(e) => setEditText(e.target.value)}
                     onKeyDown={(e) => {
                       if (e.key === "Enter") saveEdit();
                       if (e.key === "Escape") setEditingId(null);
                     }}
                     onBlur={saveEdit} />
            ) : (
              <span className="topics__text">
                {t.text}
                {t.done && t.doneVia === "auto" && (
                  <em className="topics__autochip" title="Ticked automatically — the room discussed it">auto</em>
                )}
              </span>
            )}
            {/* Delete is two-step: the ✕ arms an inline confirmation on the row (a floating
                dialog can't be used — the in-call panel also renders in the PiP pop-out). */}
            {canEdit && editingId !== t.id && confirmDel !== t.id && (
              <span className="topics__acts">
                <button type="button" title="Edit" disabled={busy}
                        onClick={() => { setEditingId(t.id); setEditText(t.text); }}>✎</button>
                <button type="button" title="Delete" disabled={busy}
                        onClick={() => setConfirmDel(t.id)}>✕</button>
              </span>
            )}
            {canEdit && confirmDel === t.id && (
              <span className="topics__confirm">
                <button type="button" className="topics__confirm-del" disabled={busy}
                        onClick={() => { setConfirmDel(null); run(() => MeetingsApi.deleteTopic(code, t.id)); }}>
                  Delete
                </button>
                <button type="button" className="topics__confirm-keep" disabled={busy}
                        onClick={() => setConfirmDel(null)}>Keep</button>
              </span>
            )}
          </li>
        ))}
      </ul>
      {canEdit && (
        <>
          <div className="topics__addrow">
            <input className="input" placeholder="Add a topic…" value={text} maxLength={300}
                   onChange={(e) => setText(e.target.value)}
                   onKeyDown={(e) => { if (e.key === "Enter") add(); }} />
            <button type="button" className="btn btn--soft btn--sm" disabled={busy || !text.trim()} onClick={add}>
              Add
            </button>
          </div>
          <div className="topics__tools">
            <button type="button" className="btn btn--soft btn--sm topics__gen" disabled={genBusy || busy} onClick={generate}
                    title="Claude proposes topics from the last meeting's notes + the channel discussion since">
              {genBusy ? "Generating…" : "✨ Suggest topics"}
            </button>
            {/* Clear all is two-step like a row delete — the in-call panel renders in the PiP pop-out,
                where a floating confirm dialog can't be used. */}
            {topics.length > 0 && !confirmClear && (
              <button type="button" className="btn btn--soft btn--sm topics__clear" disabled={busy || genBusy}
                      onClick={() => setConfirmClear(true)}>
                Clear all
              </button>
            )}
            {confirmClear && (
              <span className="topics__confirm topics__clearconfirm">
                <span className="topics__clearq">Remove all {topics.length} {topics.length === 1 ? "topic" : "topics"}?</span>
                <button type="button" className="topics__confirm-del" disabled={busy}
                        onClick={() => { setConfirmClear(false); setConfirmDel(null); run(() => MeetingsApi.clearTopics(code)); }}>
                  Clear all
                </button>
                <button type="button" className="topics__confirm-keep" disabled={busy}
                        onClick={() => setConfirmClear(false)}>Keep</button>
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/// Modal wrapper for managing a meeting's agenda from its channel card (pre-meeting).
export function MeetingTopicsModal({ code, title, canEdit, onClose }) {
  const [topics, setTopics] = useState(null); // null = loading
  const load = useCallback(() => {
    MeetingsApi.topics(code).then((t) => setTopics(t || [])).catch(() => setTopics([]));
  }, [code]);
  useEffect(() => { load(); }, [load]);

  // Portal to <body>: call sites live INSIDE message rows / the pin (which have their own
  // stacking contexts from the theme lift rules), so a nested fixed overlay would z-order
  // against sibling messages and end up BEHIND the chat. From <body> it always wins.
  return createPortal(
    <div className="modal-overlay is-open" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <header className="modal__head">
          <h3>Agenda — {title}</h3>
          <button className="modal__close" onClick={onClose}>✕</button>
        </header>
        <div className="modal__body">
          {topics === null
            ? <div className="topics__empty">Loading…</div>
            : <TopicList code={code} topics={topics} canEdit={canEdit} onMutated={load} />}
        </div>
      </div>
    </div>,
    document.body,
  );
}
